/**
 * Auswahl und Einfaerbung von PLZ-Gebieten fuer die Stauaufschlaege.
 *
 * Bewusst frei von Leaflet und Vue: das laesst sich testen, ohne eine Karte zu
 * bauen, und die Regeln stehen an einer Stelle statt verstreut in der
 * Kartenkomponente.
 *
 * Die Aufloesung eines gezeichneten Polygons zu Postleitzahlen passiert
 * vollstaendig im Browser. Das geht, weil die Karte ohnehin nur die PLZ-Gebiete
 * der Mitarbeiteradressen laedt - typischerweise einige Dutzend bis wenige
 * hundert Polygone, die zum Zeitpunkt des Zeichnens bereits vorliegen. Ein
 * Serveraufruf waere ein zusaetzlicher Roundtrip ohne Gewinn.
 */
import booleanIntersects from "@turf/boolean-intersects";

/** Ergebnis der Farbzuordnung: Stufe und zugehoerige Farbe. */
export interface SurchargeColorStep {
  key: string;
  label: string;
  color: string;
}

/**
 * Farbskala fuer die Aufschlagshoehe.
 *
 * Getrennt von der bestehenden Mitarbeiterdichte-Skala (Brauntoene): beide
 * koennen gleichzeitig sichtbar sein, und ein Anwender muss auf einen Blick
 * unterscheiden koennen, ob eine Flaeche viele Beschaeftigte oder viel Stau
 * bedeutet. Deshalb hier eine Gelb-Rot-Skala, die intuitiv fuer Verkehr steht.
 */
export const SURCHARGE_COLOR_STEPS: SurchargeColorStep[] = [
  { key: "none", label: "kein Aufschlag", color: "#e0e0e0" },
  { key: "low", label: "bis 10 %", color: "#ffe082" },
  { key: "medium", label: "bis 20 %", color: "#ffb300" },
  { key: "high", label: "bis 30 %", color: "#ef6c00" },
  { key: "veryHigh", label: "über 30 %", color: "#b71c1c" },
];

const STEP_BY_KEY = SURCHARGE_COLOR_STEPS.reduce(
  (acc, step) => {
    acc[step.key] = step;
    return acc;
  },
  {} as Record<string, SurchargeColorStep>,
);

/**
 * Aufschlagshoehe auf eine Farbstufe abbilden.
 *
 * Prozent und Minuten sind nicht direkt vergleichbar. Damit ein reiner
 * Minutenaufschlag nicht farblos bleibt, werden Minuten grob in Prozent
 * uebersetzt: 10 Minuten entsprechen hier einer Stufe. Das ist eine
 * Darstellungsheuristik, keine Rechenregel - die Zahlen selbst werden davon
 * nicht beruehrt.
 */
export function surchargeColorKey(pct: number, minutes: number): string {
  const effective = (Number(pct) || 0) + (Number(minutes) || 0);
  if (effective <= 0) return "none";
  if (effective <= 10) return "low";
  if (effective <= 20) return "medium";
  if (effective <= 30) return "high";
  return "veryHigh";
}

/** Farbe zu einer Aufschlagshoehe. */
export function surchargeColor(pct: number, minutes: number): string {
  return STEP_BY_KEY[surchargeColorKey(pct, minutes)].color;
}

type FeatureLike = { properties?: { plz?: string } | null; geometry?: unknown };
type CollectionLike = { features?: FeatureLike[] } | null | undefined;

/**
 * Postleitzahlen, deren Gebiet ein gezeichnetes Polygon beruehrt.
 *
 * Absichtlich "beruehrt" und nicht "liegt vollstaendig darin": beim Einrahmen
 * mit der Maus trifft man Gebietsgrenzen nie exakt, und ein Nutzer, der ein
 * Gebiet sichtbar mit einschliesst, erwartet es auch in der Auswahl.
 *
 * @param collection Die geladenen PLZ-Gebiete (GeoJSON-FeatureCollection)
 * @param drawnPolygon Das gezeichnete Polygon als GeoJSON-Geometrie/Feature
 * @returns Postleitzahlen ohne Doubletten, aufsteigend sortiert
 */
export function plzTouchedByPolygon(
  collection: CollectionLike,
  drawnPolygon: unknown,
): string[] {
  if (!collection?.features?.length || !drawnPolygon) return [];

  const hits = new Set<string>();
  for (const feature of collection.features) {
    const plz = feature?.properties?.plz;
    if (!plz || !feature.geometry) continue;
    try {
      if (booleanIntersects(feature as never, drawnPolygon as never)) {
        hits.add(String(plz));
      }
    } catch {
      // Eine kaputte Geometrie darf die ganze Auswahl nicht verhindern - das
      // Gebiet faellt dann einfach heraus.
      continue;
    }
  }
  return [...hits].sort();
}

/**
 * Auswahl umschalten: enthaltene PLZ raus, fehlende rein.
 *
 * Gibt eine neue Menge zurueck, damit Vue die Aenderung sicher bemerkt.
 */
export function togglePlz(selection: Set<string>, plz: string): Set<string> {
  const next = new Set(selection);
  if (next.has(plz)) {
    next.delete(plz);
  } else {
    next.add(plz);
  }
  return next;
}

/** Mehrere PLZ zur Auswahl hinzufuegen. */
export function addPlz(selection: Set<string>, plzList: string[]): Set<string> {
  const next = new Set(selection);
  for (const plz of plzList) next.add(plz);
  return next;
}

/**
 * Eingaben des Dialogs in Regeln je Verkehrsmittel uebersetzen.
 *
 * @param transports Verkehrsmittel, fuer die der Wert gelten soll
 * @param pct Prozentaufschlag
 * @param minutes Absoluter Aufschlag in Minuten
 */
export function buildSurchargeValues(
  transports: string[],
  pct: number,
  minutes: number,
): {
  transport_type: string;
  surcharge_pct: number;
  surcharge_minutes: number;
}[] {
  return transports.map((transport_type) => ({
    transport_type,
    surcharge_pct: Number(pct) || 0,
    surcharge_minutes: Number(minutes) || 0,
  }));
}
